/**
 * Calculation API Service
 *
 * Frontend API client for the VIC Tender Calculation Engine backend endpoints.
 * Handles triggering calculations and fetching cost breakdown data.
 *
 * @module calculationApiService
 */

import type { OfferCostResult, ContractCostBreakdown, UsageData } from '../types';

// Use environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * API Response type for single offer calculation
 */
type CalculateOfferResponse = {
  success: boolean;
  costData: { [termMonths: string]: ContractCostBreakdown } | null;
  errors: string[];
};

/**
 * API Response type for tender bulk calculation
 */
type CalculateTenderResponse = {
  success: boolean;
  calculated: number;
  failed: number;
  errors: Array<{ offerId: string; error: string }>;
  /**
   * Terms the costing refused to price because a contract year carried no energy rate.
   *
   * ⚠️ NOT AN ERROR, AND NOT NOTHING. IT MUST REACH THE SCREEN.
   *
   * Dropping the term is correct — a term-average rate divided by a year nobody priced is an
   * invented number. What is wrong is doing it in silence: the offer simply is not in the
   * comparison, which looks exactly like a retailer who never quoted that term.
   *
   * Reported by a tester against SmartestEnergy on RAPIDFLOW GROUP: the offer's 23-month term
   * held year_1 peak/offpeak = 0 with the real rate in year_2, so the whole offer vanished from
   * every table. They had typed the rates in by hand, saw no warning, and concluded the manual
   * entry "didn't calculate". The server had computed the reason all along and the UI dropped it.
   */
  skippedTerms?: Array<{ offerId: string; retailer: string; term: string; unpricedYears: number[] }>;
};

/** One human sentence per dropped term, ready for a toast. */
export const describeSkippedTerms = (
  skipped: CalculateTenderResponse['skippedTerms'],
): string[] =>
  (skipped ?? []).map(({ retailer, term, unpricedYears }) => {
    const months = String(term).split('_')[0];
    const years = unpricedYears.join(', ');
    const plural = unpricedYears.length > 1 ? 'Years' : 'Year';
    return `${retailer} — ${months} month term left out: no ${plural} ${years} rate`;
  });

/**
 * API Response type for fetching breakdown
 * The cost_data contains contract terms directly as { "12": {energy, environment...}, "24": {...} }
 * May also have nested contract_terms field for compatibility
 */
type GetBreakdownResponse = {
  success: boolean;
  data: {
    cost_data: { [termMonths: string]: ContractCostBreakdown } & {
      contract_terms?: { [termMonths: string]: ContractCostBreakdown };
      retailer_id?: string;
      retailer_name?: string;
      offer_id?: string;
      last_updated?: string;
      _metadata?: any;
    };
    usage_data: UsageData;
    rate_type: 'Smooth' | 'Stepped';
    usage_source: 'parsed' | 'manual' | 'override';
    calculated_at: string;
    created_at: string;
    updated_at: string;
    success: boolean;
    errors: string[];
  } | null;
};

/**
 * Trigger calculation for a single offer
 *
 * @param offerId - The offer ID to calculate costs for
 * @param usageOverride - Optional usage data to override parsed values
 * @returns Calculation result with cost data
 *
 * @example
 * ```typescript
 * const result = await calculateOfferCosts('offer-123');
 * if (result.success) {
 *   console.log('Cost data:', result.costData);
 * }
 * ```
 */
export const calculateOfferCosts = async (
  offerId: string,
  usageOverride?: UsageData
): Promise<CalculateOfferResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/scopingai/offers/${offerId}/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
      },
      body: JSON.stringify({ usageOverride }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data: CalculateOfferResponse = await response.json();
    return data;
  } catch (error: any) {
    console.error('Failed to calculate offer costs:', error);
    return {
      success: false,
      costData: null,
      errors: [error.message || 'Failed to calculate offer costs'],
    };
  }
};

/**
 * Trigger bulk calculation for all offers in a tender
 *
 * Processes all offers associated with a tender in parallel, calculating costs
 * for each and storing the results in the database.
 *
 * @param tenderId - The tender ID to calculate all offers for
 * @returns Summary of calculation results (success/failure counts)
 *
 * @example
 * ```typescript
 * const result = await calculateTenderCosts('tender-456');
 * console.log(`Calculated: ${result.calculated}, Failed: ${result.failed}`);
 * ```
 */
export const calculateTenderCosts = async (tenderId: string): Promise<CalculateTenderResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/scopingai/tenders/${tenderId}/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data: CalculateTenderResponse = await response.json();
    return data;
  } catch (error: any) {
    console.error('Failed to calculate tender costs:', error);
    return {
      success: false,
      calculated: 0,
      failed: 0,
      errors: [{ offerId: '', error: error.message || 'Failed to calculate tender costs' }],
    };
  }
};

/**
 * Normalize cost_data to ensure contract terms are at root level for UI compatibility
 * Handles both old format (contract terms at root) and new format (nested contract_terms)
 */
function normalizeCostData(costData: any): { [termMonths: string]: ContractCostBreakdown } {
  if (!costData) return {};

  // If contract_terms field exists, use it
  if (costData.contract_terms && typeof costData.contract_terms === 'object') {
    const result: { [key: string]: ContractCostBreakdown } = {};

    for (const [term, data] of Object.entries(costData.contract_terms)) {
      // Skip non-term keys (like "_metadata", "_version", etc.)
      if (term.startsWith('_') || isNaN(parseInt(term))) continue;

      result[term] = data as ContractCostBreakdown;
    }

    return result;
  }

  // Old format - contract terms at root level
  const result: { [key: string]: ContractCostBreakdown } = {};

  for (const [term, data] of Object.entries(costData)) {
    // Skip non-term keys
    if (term.startsWith('_') || isNaN(parseInt(term)) || term === 'retailer_id' || term === 'retailer_name' || term === 'offer_id' || term === 'last_updated') continue;

    result[term] = data as ContractCostBreakdown;
  }

  return result;
}

/**
 * Fetch stored cost breakdown for a specific offer
 *
 * Retrieves previously calculated cost breakdown data from the database.
 * Returns null if no calculation exists for the offer yet.
 * 
 * Normalizes the response to always have contract terms in cost_data at root level.
 *
 * @param offerId - The offer ID to fetch breakdown for
 * @returns Cost breakdown data or null if not found
 *
 * @example
 * ```typescript
 * const result = await getOfferCostBreakdown('offer-123');
 * if (result.success && result.data) {
 *   console.log('Cost data:', result.data.cost_data);
 *   console.log('Last calculated:', result.data.calculated_at);
 * }
 * ```
 */
export const getOfferCostBreakdown = async (offerId: string): Promise<GetBreakdownResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/scopingai/offers/${offerId}/breakdown`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
      },
    });

    if (!response.ok) {
      // 404 is expected if breakdown doesn't exist yet
      if (response.status === 404) {
        return {
          success: false,
          data: null,
        };
      }

      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const rawData = await response.json();

    // Normalize cost_data to ensure contract terms are accessible
    // Backend returns 'contract_terms' but UI expects 'cost_data'
    if (rawData.success && rawData.data) {
      // Map contract_terms to cost_data for compatibility
      const costDataSource = rawData.data.cost_data || rawData.data.contract_terms;
      if (costDataSource) {
        const normalizedCostData = normalizeCostData(costDataSource);
        rawData.data.cost_data = normalizedCostData;
      }
    }

    return rawData as GetBreakdownResponse;
  } catch (error: any) {
    console.error('Failed to fetch offer cost breakdown:', error);
    return {
      success: false,
      data: null,
    };
  }
};

/**
 * Fetch cost breakdowns for multiple offers at once
 *
 * @param offerIds - Array of offer IDs to fetch breakdowns for
 * @returns Map of offer IDs to their cost breakdown data
 *
 * @example
 * ```typescript
 * const breakdowns = await getMultipleOfferCostBreakdowns(['offer-1', 'offer-2']);
 * breakdowns.forEach((breakdown, offerId) => {
 *   console.log(`Offer ${offerId}:`, breakdown);
 * });
 * ```
 */
export const getMultipleOfferCostBreakdowns = async (
  offerIds: string[]
): Promise<Map<string, GetBreakdownResponse['data']>> => {
  const results = new Map<string, GetBreakdownResponse['data']>();

  // Fetch all breakdowns in parallel
  const promises = offerIds.map(async (offerId) => {
    const result = await getOfferCostBreakdown(offerId);
    return { offerId, data: result.data };
  });

  const settled = await Promise.allSettled(promises);

  settled.forEach((result) => {
    if (result.status === 'fulfilled') {
      results.set(result.value.offerId, result.value.data);
    }
  });

  return results;
};

/**
 * Response type for tender costs endpoint
 */
type TenderCostsResponse = {
  success: boolean;
  data?: {
    offers: Array<{
      offer_id: string;
      retailer_id: string;
      retailer_name: string;
      retailer_logo: string | null;
      term_months: number;
      green_percent: number;
      is_recommended: boolean;
      recommended_months: number[];
      available_terms: number[];
      has_calculations: boolean;
      costs_by_term: { [termMonths: string]: ContractCostBreakdown };
    }>;
    terms_available: number[];
  };
  error?: string;
};

/**
 * Fetch all calculated costs for a tender with retailer information
 * Optimized for the Compare & Select step - returns all offers with their costs
 * in a single query using JOINs.
 *
 * @param tenderId - The tender ID to fetch costs for
 * @param term - Optional: Filter by specific contract term in months
 * @returns All offers with their calculated costs and retailer info
 *
 * @example
 * ```typescript
 * const result = await getTenderCosts('tender-123');
 * if (result.success && result.data) {
 *   console.log('Available terms:', result.data.terms_available);
 *   result.data.offers.forEach(offer => {
 *     console.log(`${offer.retailer_name}: $${offer.costs_by_term['24']?.perYear}/year`);
 *   });
 * }
 * ```
 */
export const getTenderCosts = async (
  tenderId: string,
  term?: number
): Promise<TenderCostsResponse> => {
  try {
    let url = `${API_BASE_URL}/api/scopingai/tenders/${tenderId}/costs`;
    if (term) {
      url += `?term=${term}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data: TenderCostsResponse = await response.json();
    return data;
  } catch (error: any) {
    console.error('Failed to fetch tender costs:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch tender costs',
    };
  }
};
